import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Feedback: a member's defect report or suggestion, with the context the app adds; the
// administrator's list and answers.

export type FeedbackKind = 'DEFECT' | 'SUGGESTION';
export type FeedbackStatus = 'NEW' | 'PLANNED' | 'DONE' | 'WONT_DO';
export const FEEDBACK_STATUSES: FeedbackStatus[] = ['NEW', 'PLANNED', 'DONE', 'WONT_DO'];

/** A report as its sender sees it. */
export interface MyFeedback {
  id: string;
  createdAt: string;
  kind: FeedbackKind;
  body: string;
  page: string | null;
  status: FeedbackStatus;
  adminNote: string | null;
  updatedAt: string;
  /** When it was closed (Done or Won't do); null while open. */
  closedAt: string | null;
  /** When a closed report will be deleted (6 months after closing); null while open. */
  deletesOn: string | null;
}

/** A report as the administrator sees it, with who sent it and the context. */
export interface AdminFeedback extends MyFeedback {
  userId: string;
  userName: string;
  userEmail: string;
  choirName: string | null;
  appVersion: string | null;
  userAgent: string | null;
  screen: string | null;
  locale: string | null;
  role: string | null;
  part: string | null;
}

export interface FeedbackRequest {
  kind: FeedbackKind;
  body: string;
  choirId: string | null;
  page: string;
  appVersion: string;
  screen: string;
  locale: string;
}

const MINE = ['feedback', 'mine'] as const;

export function useMyFeedback(enabled: boolean) {
  return useQuery({ queryKey: MINE, queryFn: () => api<MyFeedback[]>('/api/me/feedback'), enabled });
}

export function useSendFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FeedbackRequest) => api<MyFeedback>('/api/feedback', { method: 'POST', json: request }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MINE }),
  });
}

export function adminFeedbackParams(status: FeedbackStatus | null, kind: FeedbackKind | null, query: string): URLSearchParams {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (kind) params.set('kind', kind);
  if (query.trim()) params.set('query', query.trim());
  return params;
}

export function useAdminFeedback(status: FeedbackStatus | null, kind: FeedbackKind | null, query: string) {
  const params = adminFeedbackParams(status, kind, query);
  return useQuery({
    queryKey: ['admin', 'feedback', params.toString()],
    queryFn: () => api<AdminFeedback[]>(`/api/admin/feedback?${params}`),
    placeholderData: (previous) => previous,
  });
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: FeedbackStatus; note: string }) =>
      api<AdminFeedback>(`/api/admin/feedback/${id}`, { method: 'PATCH', json: { status, note } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'status'] });
    },
  });
}

/** The administrator deletes a report at once (the audit log records whose it was, not its text). */
export function useDeleteFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/admin/feedback/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'status'] });
    },
  });
}
